import { cn } from "@/lib/utils/general";

const ColorSwatch = ({ className, label, value }: { 
  className: string; 
  label: string;
  value: string;
}) => (
  <div className="flex flex-col gap-2">
    <div className={cn(
      "w-20 h-20 rounded-md border shadow-sm",
      className
    )} />
    <div className="text-sm">
      <p className="font-medium">{label}</p>
      <p className="text-muted-foreground text-xs">{value}</p>
    </div>
  </div>
);

const ColorSection = ({ title, colors }: { 
  title: string;
  colors: Array<{ name: string; class: string; variable: string }>;
}) => (
  <div className="space-y-4">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {colors.map(color => (
        <ColorSwatch 
          key={color.name}
          className={color.class}
          label={color.name}
          value={color.variable}
        />
      ))}
    </div>
  </div>
);

export function StyleGuide() {
  const colorSections = [
    {
      title: "Core Colors",
      colors: [
        { name: "Background", class: "bg-background", variable: "--background" },
        { name: "Foreground", class: "bg-foreground", variable: "--foreground" },
        { name: "Primary", class: "bg-primary", variable: "--primary" },
        { name: "Primary Foreground", class: "bg-primary-foreground", variable: "--primary-foreground" },
      ]
    },
    {
      title: "UI Colors",
      colors: [
        { name: "Secondary", class: "bg-secondary", variable: "--secondary" },
        { name: "Muted", class: "bg-muted", variable: "--muted" },
        { name: "Accent", class: "bg-accent", variable: "--accent" },
        { name: "Card", class: "bg-card", variable: "--card" },
      ]
    },
    {
      title: "Sidebar Colors",
      colors: [
        { name: "Sidebar", class: "bg-sidebar", variable: "--sidebar-background" },
        { name: "Sidebar Foreground", class: "bg-sidebar-foreground", variable: "--sidebar-foreground" },
        { name: "Sidebar Primary", class: "bg-sidebar-primary", variable: "--sidebar-primary" },
        { name: "Sidebar Accent", class: "bg-sidebar-accent", variable: "--sidebar-accent" },
      ]
    },
    {
      title: "Brand Colors",
      colors: [
        { name: "Logo", class: "bg-logo", variable: "--logo" },
        { name: "ASCII", class: "bg-ascii", variable: "--ascii" },
        { name: "Tokens", class: "bg-tokens", variable: "--tokens" },
      ]
    },
    {
      title: "Feature Colors",
      colors: [
        { name: "Collaborative", class: "bg-collaborative", variable: "--collaborative" },
        { name: "Visibility", class: "bg-visibility", variable: "--visibility" },
        { name: "Universe", class: "bg-universe", variable: "--universe" },
      ]
    },
    {
      title: "Theme Colors",
      colors: [
        { name: "Theme One", class: "bg-theme-one", variable: "--theme-one" },
        { name: "Theme Two", class: "bg-theme-two", variable: "--theme-two" },
        { name: "Theme Three", class: "bg-theme-three", variable: "--theme-three" },
      ]
    },
    {
      title: "Game Colors",
      colors: [
        { name: "Game Title", class: "bg-game-title", variable: "--game-title" },
        { name: "Game Input BG", class: "bg-game-input-bg", variable: "--game-input-bg" },
        { name: "Game Input Text", class: "bg-game-input-text", variable: "--game-input-text" },
        { name: "Game Accent", class: "bg-game-accent", variable: "--game-accent" },
      ]
    },
  ];

  return (
    <div className="container py-8 space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Style Guide</h1>
        <p className="text-muted-foreground">
          Available colors and their usage in the application
        </p>
      </div>

      {colorSections.map(section => (
        <ColorSection 
          key={section.title} 
          title={section.title} 
          colors={section.colors} 
        />
      ))}

      {/* Theme Preview */}
      <div className="space-y-4 mt-8">
        <h2 className="text-lg font-semibold">Theme Preview</h2>
        <div className="grid gap-4">
          <div className="p-4 bg-card rounded-lg border">
            <h3 className="text-xl text-game-title font-instrument mb-4">
              Game on, let's build!
            </h3>
            <div className="space-y-2">
              <button className="px-4 py-2 bg-game-input-bg text-game-input-text rounded-md">
                Sample Button
              </button>
              <div className="p-4 bg-game-input-bg text-game-input-text rounded-md">
                Sample Input Area
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 